/**
 * Analytics & Intelligence Overview
 * Business KPI dashboard with revenue trends and ticket metrics
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  Ticket,
  DollarSign,
  Users,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
} from 'lucide-react';

interface KpiData {
  helpdesk: { totalTickets: number; openTickets: number; inProgressTickets: number; resolvedTickets: number; urgentOpen: number; slaBreaches: number; ticketsLast30d: number };
  revenue: { totalRevenue: number; revenueLast30d: number; revenuePrev30d: number; revenueChangePercent: number; outstandingAmount: number; overdueInvoices: number; paidInvoices: number };
  crm: { totalAccounts: number; activeAccounts: number; totalContacts: number; totalDeals: number; pipelineValue: number; dealsWon30d: number };
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendLabel,
  color = 'primary',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: number;
  trendLabel?: string;
  color?: 'primary' | 'green' | 'orange' | 'red' | 'purple';
}) {
  const colorClasses: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-100 text-green-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                <span>{Math.abs(trend)}% {trendLabel}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AnalyticsOverview() {
  const { data: kpiData, isLoading } = useQuery({
    queryKey: ['analytics-kpis'],
    queryFn: () => api.getAnalyticsKpis(),
    refetchInterval: 5 * 60 * 1000, // refresh every 5 minutes
  });

  const { data: revTrendData } = useQuery({
    queryKey: ['analytics-revenue-trend'],
    queryFn: () => api.getRevenueTrend(),
  });

  const { data: ticketTrendData } = useQuery({
    queryKey: ['analytics-ticket-trend'],
    queryFn: () => api.getTicketTrend(),
  });

  const kpis = kpiData?.data as KpiData | undefined;
  const revTrend = (revTrendData?.data || []) as Array<{ month: string; paidRevenue: number; invoiceCount: number }>;
  const ticketTrend = (ticketTrendData?.data || []) as Array<{ day: string; total: number; widgetSubmissions: number }>;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const hdStats = kpis?.helpdesk;
  const revStats = kpis?.revenue;
  const crmStats = kpis?.crm;

  // Summarize ticket trend
  const ticketTotal30d = ticketTrend.reduce((s, d) => s + d.total, 0);
  const widgetTotal30d = ticketTrend.reduce((s, d) => s + d.widgetSubmissions, 0);

  // Revenue sparkline max
  const maxRev = Math.max(...revTrend.map((r) => r.paidRevenue), 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Analytics & Intelligence</h1>
        <p className="text-muted-foreground">Business KPIs, trends, and operational insights</p>
      </div>

      {/* Revenue KPIs */}
      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Revenue</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Revenue (All Time)"
            value={formatCurrency(revStats?.totalRevenue || 0)}
            icon={DollarSign}
            color="green"
          />
          <StatCard
            title="Revenue (Last 30 Days)"
            value={formatCurrency(revStats?.revenueLast30d || 0)}
            trend={revStats?.revenueChangePercent}
            trendLabel="vs prior 30d"
            icon={TrendingUp}
            color="primary"
          />
          <StatCard
            title="Outstanding"
            value={formatCurrency(revStats?.outstandingAmount || 0)}
            subtitle={`${revStats?.overdueInvoices || 0} overdue invoices`}
            icon={Clock}
            color={revStats?.overdueInvoices ? 'orange' : 'green'}
          />
          <StatCard
            title="Invoices Paid"
            value={revStats?.paidInvoices || 0}
            icon={CheckCircle}
            color="green"
          />
        </div>
      </section>

      {/* CRM KPIs */}
      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">CRM</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Active Accounts"
            value={crmStats?.activeAccounts || 0}
            subtitle={`of ${crmStats?.totalAccounts || 0} total`}
            icon={Users}
            color="primary"
          />
          <StatCard
            title="Pipeline Value"
            value={formatCurrency(crmStats?.pipelineValue || 0)}
            icon={Briefcase}
            color="purple"
          />
          <StatCard
            title="Deals Won (30d)"
            value={crmStats?.dealsWon30d || 0}
            icon={ArrowUpRight}
            color="green"
          />
          <StatCard
            title="Total Contacts"
            value={crmStats?.totalContacts || 0}
            icon={Users}
            color="primary"
          />
        </div>
      </section>

      {/* Help Desk KPIs */}
      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Help Desk</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Open Tickets"
            value={hdStats?.openTickets || 0}
            subtitle={`${hdStats?.inProgressTickets || 0} in progress`}
            icon={Ticket}
            color={hdStats?.urgentOpen ? 'red' : 'primary'}
          />
          <StatCard
            title="Urgent & Open"
            value={hdStats?.urgentOpen || 0}
            icon={AlertTriangle}
            color={hdStats?.urgentOpen ? 'red' : 'green'}
          />
          <StatCard
            title="SLA Breaches"
            value={hdStats?.slaBreaches || 0}
            icon={Clock}
            color={hdStats?.slaBreaches ? 'red' : 'green'}
          />
          <StatCard
            title="Tickets (Last 30d)"
            value={hdStats?.ticketsLast30d || 0}
            subtitle={`${widgetTotal30d} from widget`}
            icon={Ticket}
            color="primary"
          />
        </div>
      </section>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue — Last 12 Months</CardTitle>
          </CardHeader>
          <CardContent>
            {revTrend.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No revenue data</p>
            ) : (
              <div className="space-y-2">
                {revTrend.slice(-6).map((row) => (
                  <div key={row.month} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-16 shrink-0">{row.month}</span>
                    <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-primary rounded"
                        style={{ width: `${Math.max((row.paidRevenue / maxRev) * 100, 2)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-20 text-right shrink-0">
                      {formatCurrency(row.paidRevenue)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ticket Volume */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ticket Volume — Last 30 Days</CardTitle>
          </CardHeader>
          <CardContent>
            {ticketTrend.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No ticket data</p>
            ) : (
              <>
                <div className="flex items-end gap-0.5 h-28 mb-3">
                  {ticketTrend.slice(-30).map((row) => {
                    const maxT = Math.max(...ticketTrend.map((d) => d.total), 1);
                    const height = Math.max((row.total / maxT) * 100, 4);
                    return (
                      <div key={row.day} className="flex-1 flex flex-col items-center gap-0.5" title={`${row.day}: ${row.total}`}>
                        <div
                          className="w-full bg-primary/80 rounded-sm"
                          style={{ height: `${height}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>30 days ago</span>
                  <span>Total: {ticketTotal30d} tickets</span>
                  <span>Today</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
